/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-03 12:00:00
 *
 *******************************************************/

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Button,
  Stack,
  Layout,
  TextStyle,
  Card,
  SkeletonDisplayText,
  SkeletonBodyText
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import { confirmLeavePage } from 'src/helpers';
import ConfirmLeaveModal from 'src/components/views/common/handle-leave/confirm-leave-modal';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import { isDirty } from 'redux-form';
import * as themeSetupAction from 'actions/theme-setup-v3-actions';
import { TypeScreen } from './index';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { Field, reduxForm } from 'redux-form';
import ConfirmRevertThemeV3Modal from './confirm-revert-theme-v3-modal';
import { checkFeatureInstalled } from 'src/components/containers/theme-setup-v3/theme-setup-form';
import { routes } from 'src/routes';

type Props = {
  installStatus: string,
  installAppStatus: string,
  themeSetupV2: boolean
};

type ComposedProps = Props & WithI18nProps;

type State = {
  step: number,
  activeModalConfirmLeavePage: boolean,
  activeModalContactUs: boolean,
  activeModalConfirmRevertTheme: boolean,
  currentFeatureSelected: Object,
  featuresSelected: Array<string>,
  isRevertable: boolean
};

class ThemeSetupScreenRevertForm extends Component<ComposedProps, State> {

  state = {
    step: 1,
    activeModalConfirmLeavePage: false,
    activeModalContactUs: false,
    activeModalConfirmRevertTheme: false,
    currentFeatureSelected: {},
    featuresSelected: [],
    isRevertable: false,
    themeCheck: null
  };

  onSelectFeature = (feature, value) => {
    const { currentFeatureSelected } = this.state;
    currentFeatureSelected[feature] = value;
    const isRevertable = Object.keys(currentFeatureSelected).some(key => currentFeatureSelected[key]);
    const featuresSelected = Object.keys(currentFeatureSelected).filter(key => currentFeatureSelected[key]);

    this.setState({
      currentFeatureSelected,
      isRevertable,
      featuresSelected
    });
  }

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  handleClickCancel = () => {
    this.confirmLeave(TypeScreen.List)
  }

  componentDidMount() {
    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { generalFormDirty, i18n } = this.props;
    return confirmLeavePage(event, generalFormDirty, i18n);
  };

  // Close modal
  closeModal = () => {
    this.setState({ activeModalConfirmLeavePage: false, actionToGo: () => {}, params: {} });
  };

  // Handle confirm leave modal
  // Discard change, continue action
  handleDiscard = () => {
    const { actionToGo } = this.state;
    actionToGo();

    // Close modal
    this.closeModal();
  };

  // Handle confirm leave
  confirmLeave = (action, params = {}) => {
    const { generalFormDirty, handleThemeSetupChangeScreen } = this.props;
    let callBack = (params = {}) => {};
    switch (action) {
      case TypeScreen.List: {
        callBack = () => handleThemeSetupChangeScreen(TypeScreen.List)
        break;
      }
      default:
    }
    if (generalFormDirty) {
      this.setState({ activeModalConfirmLeavePage: true, actionToGo: callBack, params: params });
    } else {
      // $FlowFixMe
      callBack(params);
    }
  };

  handleRevertComplete = () => {
    const { handleThemeSetupChangeScreen } = this.props;
    handleThemeSetupChangeScreen(TypeScreen.List)
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.themeCheck && !nextProps.checkThemeLoading) {
      this.setState({
        themeCheck: nextProps.themeCheck
      })
    }
  }

  render() {
    const {
      activeModalConfirmLeavePage,
      activeModalConfirmRevertTheme,
      isRevertable,
      featuresSelected,
      themeCheck
    } = this.state;
    const { i18n, history, theme, checkThemeLoading } = this.props;

    const {
      isCheckedFeatureCollection,
      isCheckedFeatureSearch,
      isCheckedFeatureInstant,
      isCheckedFeatureRecommendation
    } = checkFeatureInstalled(themeCheck);

    return (
      <div>
        <Page
          title={
            <div className="theme-setup-heading">
              <Stack spacing="tight">
                <span>{i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.themeRevert_title')}</span>
              </Stack>
            </div>
          }
          breadcrumbs={[
            {
              content: i18n.translate('AdminHTML.Recommendation.WidgetItem.breadcrumb'),
              onAction: () => this.confirmLeave(TypeScreen.List)
            }
          ]}
          primaryAction={
            <div className='theme-setup-menu'>
              <div className="theme-setup-menu-action">
                <Button onClick={this.handleClickCancel}>{i18n.translate('AdminHTML.Common.Action.cancel')}</Button>
                <Button primary disabled={isRevertable ? false : true} onClick={() => this.toggleChangeState('activeModalConfirmRevertTheme')}>{i18n.translate('AdminHTML.ThemeSetup.ConfirmRevertThemeModal.action_Continue')}</Button>
              </div>
            </div>
          }>
          <Layout>
            <Layout.Section>
            </Layout.Section>
            <Layout.Section>
            <Card>
              <Card.Section>
                <div className="BC-Theme-Setup_Form-Item" style={{ paddingTop: '0.5625rem' }}>
                  <div className="title" style={{ marginBottom: '15px' }}>
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.themeRevert_content1')}
                    </TextStyle>
                  </div>
                  <div>
                  </div>
                  {
                    checkThemeLoading ? 
                    <div className="list-features-select">
                      <SkeletonDisplayText size="medium" />
                      <SkeletonBodyText />
                    </div> :
                    <div className="list-features-select">
                      {
                        isCheckedFeatureCollection && 
                        <Field
                          name="feature.filterCollection"
                          component={RenderCheckbox}
                          onChange={(e) => this.onSelectFeature('filterCollection', e)}
                          label={i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature_Collection')}
                        />
                      }
                      {
                        isCheckedFeatureSearch && 
                        <Field
                          name="feature.filterSearch"
                          component={RenderCheckbox}
                          onChange={(e) => this.onSelectFeature('filterSearch', e)}
                          label={i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature_Search')}
                        />
                      }
                      {
                        isCheckedFeatureInstant && 
                        <Field
                          name="feature.instantSearch"
                          component={RenderCheckbox}
                          onChange={(e) => this.onSelectFeature('instantSearch', e)}
                          label={i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature_Instant')}
                        />
                      }
                      {
                        isCheckedFeatureRecommendation && 
                        <Field
                          name="feature.recommendation"
                          component={RenderCheckbox}
                          onChange={(e) => this.onSelectFeature('recommendation', e)}
                          label={i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature_Recommendation')}
                        />
                      }
                    </div>
                  }
                </div>
              </Card.Section>
            </Card>
            </Layout.Section>
            <Layout.Section>
              <div className='theme-setup-menu'>
                <div className="theme-setup-menu-action">
                  <Button onClick={this.handleClickCancel}>{i18n.translate('AdminHTML.Common.Action.cancel')}</Button>
                  <Button primary disabled={isRevertable ? false : true} onClick={() => this.toggleChangeState('activeModalConfirmRevertTheme')}>{i18n.translate('AdminHTML.ThemeSetup.ConfirmRevertThemeModal.action_Continue')}</Button>
                </div>
              </div>
            </Layout.Section>
          </Layout>
          <ConfirmRevertThemeV3Modal
            theme={theme}
            features={featuresSelected}
            active={activeModalConfirmRevertTheme}
            handleClose={() => this.toggleChangeState('activeModalConfirmRevertTheme')}
            handleRevertComplete={this.handleRevertComplete}
          />
          {/* This modal is used for when click any element on this page, but route not change */}
          <ConfirmLeaveModal
            active={activeModalConfirmLeavePage}
            handleCancel={this.closeModal}
            handleDiscard={this.handleDiscard}
          />
          {/* This modal is used for when click any link on this page, the route will change */}
          <RouteLeavingGuard
            when={true}
            navigate={path => {
              if (path === routes.themeSetup.view()) {
                this.props.handleThemeSetupChangeScreen(TypeScreen.List)
              } else {
                history.push(path);
              }
            }}
            isGotoWithoutShowModalConfirm={true}
            shouldBlockNavigation={true}
          />
        </Page>
      </div>
    )
  }
}

const ThemeSetupScreenRevert = reduxForm({
  form: 'ThemeRevert',
  enableReinitialize: true,
})(ThemeSetupScreenRevertForm);

const mapStateToProps = (state, props) => {
  const { themeSetupV3 = {} } = state;
  const generalFormDirty = isDirty('ThemeSetup')(state);
  const setupThemeSuccess = _.get(state.themeSetupV3, 'setupThemeSuccess', undefined);

  return {
    generalFormDirty,
    setupThemeSuccess,

    checkThemeLoading: _.get(themeSetupV3, 'checkThemeLoading', false),
    themeCheck: _.get(themeSetupV3, 'themeCheck', null),
    themeCheckId: _.get(themeSetupV3, 'themeCheckId', null),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getRecommendationWidgets: () =>
  dispatch(recommendationAction.getRecommendationWidgets(ownProps.i18n)),
  setupThemeV3: (body: Object) => dispatch(themeSetupAction.setupThemeV3(body, ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ThemeSetupScreenRevert));
